import dayjs, { Dayjs } from 'dayjs';

export interface ZohoTimeEntry {
  time_entry_id: string;
  project_id: string;
  project_name: string;
  customer_id: string;
  customer_name: string;
  task_id: string;
  task_name: string;
  user_id: string;
  user_name: string;
  log_date: string; // "YYYY-MM-DD"
  log_time: string; // "HH:mm"
  hours: number;
  minutes: number;
  time_spent_in_minutes: number;
  is_billable: boolean;
  notes: string;
  invoice_id?: string;
}

export interface ZohoTimeEntriesRoot {
  code: number;
  message: string;
  time_entries: ZohoTimeEntry[];
}

export interface ZohoTimeEntriesByDate {
  date: Date;
  totalHours: number;
  timeEntries: ZohoTimeEntry[];
}

export interface ZohoTimeEntriesByWeek {
  startDateOfWeek: Date;
  endDateOfWeek: Date;
  totalHours: number;
  timeEntriesByDay: ZohoTimeEntriesByDate[];
}

export interface ZohoTimeEntriesByMonth {
  month: string;
  year: number;
  totalHours: number;
  timeEntriesByDay: ZohoTimeEntriesByDate[];
}

export function groupZohoEntriesByDate(entries: ZohoTimeEntry[]): { grouped: ZohoTimeEntriesByDate[]; totalHours: number } {
  const map = new Map<string, ZohoTimeEntry[]>();

  for (const entry of entries) {
    const key = entry.log_date;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(entry);
  }

  let totalHours = 0;
  const grouped: ZohoTimeEntriesByDate[] = Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateStr, dayEntries]) => {
      const dayTotal = dayEntries.reduce((sum, e) => {
        const [h, m] = e.log_time.split(':').map(Number);
        return sum + h + m / 60;
      }, 0);
      totalHours += dayTotal;
      return { date: dayjs(dateStr).toDate(), totalHours: dayTotal, timeEntries: dayEntries };
    });

  return { grouped, totalHours };
}

export function groupZohoEntriesByWeek(byDate: ZohoTimeEntriesByDate[]): ZohoTimeEntriesByWeek[] {
  const weekMap = new Map<string, ZohoTimeEntriesByWeek>();

  for (const day of byDate) {
    const d = dayjs(day.date);
    const weekStart = d.isoWeekday(1).startOf('day');
    const weekEnd = d.isoWeekday(7).startOf('day');
    const key = weekStart.format('YYYY-MM-DD');

    if (!weekMap.has(key)) {
      weekMap.set(key, {
        startDateOfWeek: weekStart.toDate(),
        endDateOfWeek: weekEnd.toDate(),
        totalHours: 0,
        timeEntriesByDay: [],
      });
    }

    const week = weekMap.get(key)!;
    week.timeEntriesByDay.push(day);
    week.totalHours += day.totalHours;
  }

  return Array.from(weekMap.values());
}

export function groupZohoEntriesByMonth(byDate: ZohoTimeEntriesByDate[]): ZohoTimeEntriesByMonth[] {
  const monthMap = new Map<string, ZohoTimeEntriesByMonth>();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  for (const day of byDate) {
    const d = dayjs(day.date);
    const key = d.format('YYYY-MM');

    if (!monthMap.has(key)) {
      monthMap.set(key, {
        month: monthNames[d.month()],
        year: d.year(),
        totalHours: 0,
        timeEntriesByDay: [],
      });
    }

    const month = monthMap.get(key)!;
    month.timeEntriesByDay.push(day);
    month.totalHours += day.totalHours;
  }

  return Array.from(monthMap.values());
}
